import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const empty = {
  title: "", author: "", description: "", price: "", cover_image: "",
  category: "", formats: "", sku: "", isbn: "", language: "",
  condition: "", pages: "", stock: "", featured: false,
  shipping_price: "", handling_days: "", delivery_min_days: "", delivery_max_days: "",
  // Enterprise catalog
  primary_category_id: "", tags: "",
  publisher: "", publication_year: "", edition: "",
  series: "", volume_number: "",
  isbn_10: "", isbn_13: "",
  audience: "", reading_level: "",
  weight: "", dimensions: "", country_of_publication: "",
  // AI enrichment
  seo_description: "", meta_description: "", seo_keywords: "",
};

export default function EditBookDialog({ book, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [categoriesList, setCategoriesList] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/admin/categories/flat").then((r) => setCategoriesList(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!book) return;
    setForm({
      title: book.title || "",
      author: book.author || "",
      description: book.description || "",
      price: book.price ?? "",
      cover_image: book.cover_image || "",
      category: book.category || "",
      formats: Array.isArray(book.formats) ? book.formats.join(", ") : (book.formats || ""),
      sku: book.sku || "",
      isbn: book.isbn || "",
      language: book.language || "",
      condition: book.condition || "",
      pages: book.pages ?? "",
      stock: book.stock ?? "",
      featured: !!book.featured,
      shipping_price: book.shipping_price ?? "",
      handling_days: book.handling_days ?? "",
      delivery_min_days: book.delivery_min_days ?? "",
      delivery_max_days: book.delivery_max_days ?? "",
      // catalog
      primary_category_id: book.primary_category_id || "",
      tags: Array.isArray(book.tags) ? book.tags.join(", ") : (book.tags || ""),
      publisher: book.publisher || "",
      publication_year: book.publication_year ?? "",
      edition: book.edition || "",
      series: book.series || "",
      volume_number: book.volume_number || "",
      isbn_10: book.isbn_10 || "",
      isbn_13: book.isbn_13 || "",
      audience: book.audience || "",
      reading_level: book.reading_level || "",
      weight: book.weight || "",
      dimensions: book.dimensions || "",
      country_of_publication: book.country_of_publication || "",
      // AI enrichment
      seo_description: book.seo_description || "",
      meta_description: book.meta_description || "",
      seo_keywords: Array.isArray(book.seo_keywords) ? book.seo_keywords.join(", ") : (book.seo_keywords || ""),
    });
  }, [book]);

  const uploadImage = async (file) => {
    if (!file) return;
    const okTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!okTypes.includes(file.type)) {
      toast.error("Please choose a JPG, PNG, GIF, or WEBP image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, cover_image: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!book) return;
    setSaving(true);
    try {
      const payload = {};
      const strFields = ["title", "author", "description", "cover_image", "category",
        "sku", "isbn", "language", "condition"];
      for (const k of strFields) {
        const v = (form[k] ?? "").trim();
        if (v !== "") payload[k] = v;
      }
      // Price
      if (form.price !== "" && form.price !== null) {
        const p = parseFloat(form.price);
        if (Number.isFinite(p)) payload.price = p;
      }
      // Stock
      if (form.stock !== "" && form.stock !== null) {
        const s = parseInt(form.stock, 10);
        if (Number.isFinite(s)) payload.stock = s;
      }
      // Shipping price
      if (form.shipping_price !== "" && form.shipping_price !== null) {
        const sp = parseFloat(form.shipping_price);
        if (Number.isFinite(sp)) payload.shipping_price = sp;
      }
      // Shipping time overrides (blank = use global policy)
      for (const k of ["handling_days", "delivery_min_days", "delivery_max_days"]) {
        if (form[k] !== "" && form[k] !== null) {
          const n = parseInt(form[k], 10);
          if (Number.isFinite(n) && n >= 0) payload[k] = n;
        }
      }
      // Blank fields that previously held an override → clear back to global policy
      const clearable = ["shipping_price", "handling_days", "delivery_min_days", "delivery_max_days"];
      const clearFields = clearable.filter((k) => (form[k] === "" || form[k] === null) && book?.[k] != null);
      if (clearFields.length) payload.clear_fields = clearFields;
      // Pages
      if (form.pages !== "" && form.pages !== null) {
        const p = parseInt(form.pages, 10);
        if (Number.isFinite(p)) payload.pages = p;
      }
      // Formats
      if (typeof form.formats === "string" && form.formats.trim()) {
        payload.formats = form.formats.split(",").map((s) => s.trim()).filter(Boolean);
      }
      payload.featured = !!form.featured;

      // ---- Enterprise catalog fields ----
      if (form.primary_category_id) payload.primary_category_id = form.primary_category_id;
      if (typeof form.tags === "string") {
        const arr = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
        if (arr.length || book?.tags?.length) payload.tags = arr;
      }
      if (form.publication_year !== "" && form.publication_year !== null) {
        const y = parseInt(form.publication_year, 10);
        if (Number.isFinite(y)) payload.publication_year = y;
      }
      for (const k of ["publisher", "edition", "series", "volume_number",
                        "isbn_10", "isbn_13", "audience", "reading_level",
                        "weight", "dimensions", "country_of_publication",
                        "seo_description", "meta_description"]) {
        if (form[k] !== undefined && form[k] !== null && String(form[k]).trim() !== "") {
          payload[k] = form[k];
        }
      }
      if (typeof form.seo_keywords === "string") {
        const arr = form.seo_keywords.split(",").map((t) => t.trim()).filter(Boolean);
        if (arr.length || book?.seo_keywords?.length) payload.seo_keywords = arr;
      }

      await api.put(`/books/${book.id}`, payload);
      toast.success("Book updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      const raw = err.response?.data?.detail;
      let msg = "Save failed";
      if (typeof raw === "string") msg = raw;
      else if (Array.isArray(raw)) msg = raw.map((r) => `${(r.loc || []).slice(-1)[0] || "field"}: ${r.msg}`).join(" · ");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#F9F6F0] rounded-none border-[#E5E1D8] max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="edit-book-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">Edit listing</DialogTitle>
          <DialogDescription className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
            {book?.id ? `#${book.id.slice(0, 8)}` : ""}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3" data-testid="edit-book-form">
          {/* Cover image preview + upload */}
          <div className="flex gap-4 items-start border border-[#E5E1D8] p-3">
            <div className="w-24 h-32 bg-[#EFEBE1] flex items-center justify-center overflow-hidden shrink-0">
              {form.cover_image ? (
                <img
                  src={form.cover_image}
                  alt="Cover preview"
                  className="w-full h-full object-cover"
                  data-testid="edit-book-cover-preview"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <span className="font-mono text-[9px] uppercase tracking-editorial text-[#1A1714]/50">
                  No image
                </span>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
                Cover image
              </p>
              <Input
                data-testid="edit-book-cover-url"
                placeholder="Paste image URL"
                value={form.cover_image}
                onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                className="rounded-none"
              />
              <div className="flex gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  data-testid="edit-book-file-input"
                  onChange={(e) => uploadImage(e.target.files?.[0])}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="edit-book-upload-btn"
                  className="rounded-none border-[#1A1714] font-mono text-[10px] uppercase tracking-editorial"
                >
                  {uploading ? (
                    <>
                      <RefreshCw size={12} className="mr-2 animate-spin" /> Uploading…
                    </>
                  ) : (
                    <>
                      <Upload size={12} className="mr-2" /> Upload image
                    </>
                  )}
                </Button>
                {form.cover_image && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setForm({ ...form, cover_image: "" })}
                    data-testid="edit-book-clear-cover"
                    className="rounded-none border-[#1A1714]/40 text-[#1A1714]/60 font-mono text-[10px] uppercase tracking-editorial"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <p className="font-mono text-[9px] uppercase tracking-editorial text-[#1A1714]/40">
                JPG, PNG, GIF, WEBP · max 5 MB
              </p>
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Title *</label>
            <Input
              required
              placeholder="e.g. Salt Houses"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1 rounded-none"
              data-testid="edit-book-title"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Author *</label>
            <Input
              required
              placeholder="e.g. Hala Alyan"
              value={form.author}
              onChange={(e) => setForm({ ...form, author: e.target.value })}
              className="mt-1 rounded-none"
              data-testid="edit-book-author"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Description</label>
            <Textarea
              placeholder="A short blurb shown on the book detail page"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 rounded-none min-h-24"
              data-testid="edit-book-description"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Price (USD) *</label>
              <Input
                required
                type="number"
                step="0.01"
                placeholder="e.g. 18.00"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="mt-1 rounded-none"
                data-testid="edit-book-price"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Shipping (USD)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="Global default"
                value={form.shipping_price}
                onChange={(e) => setForm({ ...form, shipping_price: e.target.value })}
                className="mt-1 rounded-none"
                data-testid="edit-book-shipping"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Stock (quantity)</label>
              <Input
                type="number"
                placeholder="e.g. 12"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                className="mt-1 rounded-none"
                data-testid="edit-book-stock"
              />
            </div>
          </div>

          {/* Per-book shipping time overrides — blank means the global policy applies */}
          <div className="grid grid-cols-3 gap-4" data-testid="edit-book-shipping-times">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Handling days</label>
              <Input
                type="number"
                min="0"
                placeholder="Global"
                value={form.handling_days}
                onChange={(e) => setForm({ ...form, handling_days: e.target.value })}
                className="mt-1 rounded-none"
                data-testid="edit-book-handling-days"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Delivery min days</label>
              <Input
                type="number"
                min="0"
                placeholder="Global"
                value={form.delivery_min_days}
                onChange={(e) => setForm({ ...form, delivery_min_days: e.target.value })}
                className="mt-1 rounded-none"
                data-testid="edit-book-delivery-min"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Delivery max days</label>
              <Input
                type="number"
                min="0"
                placeholder="Global"
                value={form.delivery_max_days}
                onChange={(e) => setForm({ ...form, delivery_max_days: e.target.value })}
                className="mt-1 rounded-none"
                data-testid="edit-book-delivery-max"
              />
            </div>
          </div>
          <p className="text-[11px] text-[#1A1714]/50 -mt-2">
            Leave blank to inherit the global shipping policy (Admin → Shipping).
          </p>

          {/* Ingram price breakdown (read-only, only shown once the book has been synced) */}
          {(book?.ingram_list_price != null || book?.purchase_price != null) && (
            <div className="border border-[#E5E1D8] p-4 space-y-1.5" data-testid="edit-book-price-breakdown">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Ingram price breakdown</p>
                {book?.ingram_last_synced_at && (
                  <p className="font-mono text-[10px] text-[#1A1714]/40">
                    synced {new Date(book.ingram_last_synced_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              {[
                ["Ingram list price", book.ingram_list_price],
                ["Discount applied", book.ingram_discount_percent != null ? `${book.ingram_discount_percent}%` : null],
                ["Purchase price", book.purchase_price],
                ["Profit margin added", book.margin_added],
                ["Stripe fee added", book.stripe_added],
                ["Final sale price", book.final_sale_price],
              ].map(([label, value]) => {
                if (value === null || value === undefined || value === "") return null;
                const isMoney = typeof value === "number" || (typeof value === "string" && !value.endsWith("%"));
                const display = isMoney ? `$${Number(value).toFixed(2)}` : value;
                const isFinal = label === "Final sale price";
                return (
                  <div key={label} className={"flex justify-between text-sm " + (isFinal ? "font-semibold border-t border-[#E5E1D8]/70 pt-1.5 mt-1.5" : "")}>
                    <span className="text-[#1A1714]/70">{label}</span>
                    <span className={"font-mono " + (isFinal ? "text-[#1A1714]" : "text-[#1A1714]/80")}>{display}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Category</label>
              <Input
                placeholder="e.g. Fiction, Poetry"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1 rounded-none"
                data-testid="edit-book-category"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Condition</label>
              <Input
                placeholder="e.g. New, Like New"
                value={form.condition}
                onChange={(e) => setForm({ ...form, condition: e.target.value })}
                className="mt-1 rounded-none"
                data-testid="edit-book-condition"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">SKU</label>
              <Input
                placeholder="Internal stock keeping unit"
                value={form.sku}
                onChange={(e) => setForm({ ...form, sku: e.target.value })}
                className="mt-1 rounded-none"
                data-testid="edit-book-sku"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">ISBN</label>
              <Input
                placeholder="e.g. 9780544912588"
                value={form.isbn}
                onChange={(e) => setForm({ ...form, isbn: e.target.value })}
                className="mt-1 rounded-none"
                data-testid="edit-book-isbn"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Language</label>
              <Input
                placeholder="e.g. English"
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
                className="mt-1 rounded-none"
                data-testid="edit-book-language"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Pages</label>
              <Input
                type="number"
                placeholder="e.g. 320"
                value={form.pages}
                onChange={(e) => setForm({ ...form, pages: e.target.value })}
                className="mt-1 rounded-none"
                data-testid="edit-book-pages"
              />
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
              Formats
            </label>
            <Input
              placeholder="e.g. Paperback, Hardcover, Ebook, Audiobook"
              value={form.formats}
              onChange={(e) => setForm({ ...form, formats: e.target.value })}
              className="mt-1 rounded-none"
              data-testid="edit-book-formats"
            />
            <p className="mt-1 font-mono text-[9px] uppercase tracking-editorial text-[#1A1714]/40">
              Comma-separated.
            </p>
          </div>

          <label className="flex items-center gap-3 pt-1">
            <Checkbox
              checked={!!form.featured}
              onCheckedChange={(v) => setForm({ ...form, featured: !!v })}
              data-testid="edit-book-featured"
            />
            <span className="font-mono text-[11px] uppercase tracking-editorial">
              Feature on homepage
            </span>
          </label>

          {/* ---- Enterprise catalog fields ---- */}
          <div className="pt-3 border-t border-[#E5E1D8]">
            <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mb-2">
              Categorization
            </p>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Primary category</label>
              <select
                value={form.primary_category_id}
                onChange={(e) => setForm({ ...form, primary_category_id: e.target.value })}
                data-testid="edit-book-primary-cat"
                className="w-full mt-1 h-10 rounded-none border border-[#E5E1D8] bg-white px-3 text-sm">
                <option value="">— None —</option>
                {categoriesList.map((c) => (
                  <option key={c.id} value={c.id}>{"— ".repeat(c.level)}{c.name}</option>
                ))}
              </select>
            </div>
            <div className="mt-2">
              <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Tags (comma-separated)</label>
              <Input
                placeholder="Bestseller, Award Winner, Book Club Pick"
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                className="mt-1 rounded-none"
                data-testid="edit-book-tags"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            data-testid="edit-book-toggle-advanced"
            className="w-full text-left font-mono text-[10px] uppercase tracking-editorial text-[#7A2021] hover:underline pt-2"
          >
            {showAdvanced ? "▾" : "▸"} Advanced fields (bibliographic + SEO)
          </button>

          {showAdvanced && (
            <div className="space-y-3 border-l-2 border-[#E5E1D8] pl-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Publisher</label>
                  <Input value={form.publisher} onChange={(e) => setForm({ ...form, publisher: e.target.value })}
                    className="mt-1 rounded-none" data-testid="edit-book-publisher" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Publication year</label>
                  <Input type="number" value={form.publication_year} onChange={(e) => setForm({ ...form, publication_year: e.target.value })}
                    className="mt-1 rounded-none" data-testid="edit-book-pub-year" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Edition</label>
                  <Input value={form.edition} onChange={(e) => setForm({ ...form, edition: e.target.value })}
                    className="mt-1 rounded-none" data-testid="edit-book-edition" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Country of publication</label>
                  <Input value={form.country_of_publication} onChange={(e) => setForm({ ...form, country_of_publication: e.target.value })}
                    className="mt-1 rounded-none" data-testid="edit-book-country" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Series</label>
                  <Input value={form.series} onChange={(e) => setForm({ ...form, series: e.target.value })}
                    className="mt-1 rounded-none" data-testid="edit-book-series" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Volume #</label>
                  <Input value={form.volume_number} onChange={(e) => setForm({ ...form, volume_number: e.target.value })}
                    className="mt-1 rounded-none" data-testid="edit-book-volume" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">ISBN-10</label>
                  <Input value={form.isbn_10} onChange={(e) => setForm({ ...form, isbn_10: e.target.value })}
                    className="mt-1 rounded-none font-mono text-xs" data-testid="edit-book-isbn10" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">ISBN-13</label>
                  <Input value={form.isbn_13} onChange={(e) => setForm({ ...form, isbn_13: e.target.value })}
                    className="mt-1 rounded-none font-mono text-xs" data-testid="edit-book-isbn13" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Audience</label>
                  <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}
                    data-testid="edit-book-audience"
                    className="w-full mt-1 h-10 rounded-none border border-[#E5E1D8] bg-white px-3 text-sm">
                    <option value="">— Any —</option>
                    <option value="Children">Children</option>
                    <option value="Teen">Teen</option>
                    <option value="Adult">Adult</option>
                    <option value="Academic">Academic</option>
                    <option value="Professional">Professional</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Reading level</label>
                  <select value={form.reading_level} onChange={(e) => setForm({ ...form, reading_level: e.target.value })}
                    data-testid="edit-book-reading-level"
                    className="w-full mt-1 h-10 rounded-none border border-[#E5E1D8] bg-white px-3 text-sm">
                    <option value="">— Any —</option>
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Weight</label>
                  <Input placeholder="e.g. 1.2 lb" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })}
                    className="mt-1 rounded-none" data-testid="edit-book-weight" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Dimensions</label>
                  <Input placeholder="e.g. 6 x 9 x 1 in" value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })}
                    className="mt-1 rounded-none" data-testid="edit-book-dimensions" />
                </div>
              </div>

              <div className="pt-3 border-t border-[#E5E1D8]">
                <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mb-2">
                  SEO (AI enrichment)
                </p>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Meta description (≤155 chars)</label>
                  <Textarea value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                    className="mt-1 rounded-none" data-testid="edit-book-meta-desc" />
                </div>
                <div className="mt-2">
                  <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">SEO long description</label>
                  <Textarea value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })}
                    className="mt-1 rounded-none" data-testid="edit-book-seo-desc" />
                </div>
                <div className="mt-2">
                  <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">SEO keywords (comma-separated)</label>
                  <Input value={form.seo_keywords} onChange={(e) => setForm({ ...form, seo_keywords: e.target.value })}
                    className="mt-1 rounded-none" data-testid="edit-book-seo-keywords" />
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button
              type="submit"
              disabled={saving}
              data-testid="edit-book-save"
              className="flex-1 rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial"
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="edit-book-cancel"
              className="rounded-none border-[#1A1714] font-mono text-xs uppercase tracking-editorial"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
